import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import 'jspdf-autotable';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Button,
} from '@mui/material';

import {
  useLogUsageHistoryQuery,
  useLazyLogUsageHistoryQuery,
} from '../../services/usageHistory';
import checkAccessRights from '../../utils/functions/accessRight';
import { ROLES } from '../../utils/constants';
import addCommas from '../../utils/functions/commas';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles'; // 1. ต้อง import 'styled'
import SortableTableLabel from '../../components/Table/SortableTableLabel';



const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#fff', // หรือ theme.palette.action.hover ถ้าต้องการ
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },

  // [ปรับปรุง] ย้าย :hover มาไว้ที่นี่
  '&:hover': {
    
    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
    position: 'relative',
    zIndex: 10,
    cursor: 'pointer', // เพิ่ม cursor เพื่อให้รู้ว่า hover ได้
  },

  transition: 'transform 0.15s ',
}));

const customCellHeader = {
  whiteSpace: 'nowrap',
  backgroundColor: 'primary.main',
  color: 'white',
  textAlign: 'center', // จัดข้อความกึ่งกลางแนวนอน
  minWidth: '180px', // กำหนดความกว้างขั้นต่ำ
  width: '200px', // กำหนดความกว้างของเซลล์
  maxWidth: '300px', // กำหนดความกว้างสูงสุด (ไม่ให้เกินขนาดที่กำหนด)
};
const customCell = {
  whiteSpace: 'nowrap',
  textAlign: 'center', // จัดข้อความกึ่งกลางแนวนอน
};

function Report() {
  const { t } = useTranslation();
  const { value: merchant, allMerchant } = useSelector(
    (state) => state.merchant,
  );
  const isAdminRole = checkAccessRights(ROLES.ADMIN_ROLE);
  const sorgcode = isAdminRole ? merchant || '' : allMerchant || '';
  const [rowsPerPage, setRowsPerPage] = useState(10); // จำนวนแถวต่อหน้า
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(10);
  const [reportData, setReportData] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const shouldSkip = isAdminRole && !merchant;

  const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));

  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const columns = [
    { id: 'upload_ref', label: t('app.uploadRef') },
    { id: 'transaction_ref', label: t('app.transactionRefNo') },
    { id: 'invoice_no', label: t('app.invoiceNo') },
    { id: 'customer_name', label: t('app.customerName') },
    { id: 'financer', label: t('app.financeOfficer'), sortable: false },
    { id: 'grand_total', label: t('app.grandTotal') },
    { id: 'payment_date', label: t('app.paymentDate') },
    { id: 'import_date', label: t('app.importDate') },
    { id: 'upload_by', label: t('app.uploadBy') },
    { id: 'status', label: t('app.status') },
    { id: 'edit_at', label: t('app.editAt') },
    { id: 'status_date', label: t('app.statusDate') },
    { id: 'owner_by', label: t('app.financeOfficer') },
    { id: 'notes', label: t('app.remark') },
  ];

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue ? dayjs(newValue).format('YYYY-MM-DD') : null);
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue ? dayjs(newValue).format('YYYY-MM-DD') : null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0); // เริ่มที่หน้า 0 เมื่อเปลี่ยนจำนวนแถวต่อหน้า
  };

  const handleRequestSort = (property) => {
    setSortOrder(
      sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc',
    );
    setSortBy(property);
    setPage(0);
  };

  const TYPE = 'evidence';

  const {
    data: LogHistory,
    isLoading,
    isFetching,
  } = useLogUsageHistoryQuery(
    {
      sorgcode,
      page: page === 0 ? 1 : page + 1,
      size: rowsPerPage,
      endDate: endDate,
      startDate: startDate,
      type: TYPE,
      sortBy,
      sortOrder,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    },
  );

  useEffect(() => {
    setReportData(LogHistory?.data);

    if (LogHistory) {
      const { pagination } = LogHistory;

      setTotal(pagination.total);
    }
  }, [LogHistory]);

  const [LogUsageHistory] = useLazyLogUsageHistoryQuery();

  const generateExcel = async () => {
    if (isExporting || shouldSkip) return;
    if (sorgcode === '-') {
      alert('กรุณาเลือก Vendor');
      return;
    }

    setIsExporting(true);
    try {
      // Fetch every page using the current filters, independently of table pagination.
      const exportRows = [];
      const exportSize = 1000;
      let exportPage = 1;
      let exportTotal;
      do {
        const result = await LogUsageHistory({
          sorgcode,
          startDate,
          endDate,
          type: TYPE,
          page: exportPage,
          size: exportSize,
          sortBy,
          sortOrder,
        }).unwrap();

        if (!Array.isArray(result?.data) ||
            !Number.isInteger(result?.pagination?.total) ||
            result.pagination.total < 0) {
          throw new Error('Invalid activity history response');
        }
        exportTotal = result.pagination.total;
        if (result.data.length === 0 && exportRows.length < exportTotal) {
          throw new Error('Incomplete activity history response');
        }
        exportRows.push(...result.data);
        exportPage += 1;
      } while (exportRows.length < exportTotal);

      if (exportRows.length === 0) {
        alert('ไม่มีข้อมูลให้ดาวน์โหลด');
        return;
      }

      // กำหนดคอลัมน์ให้ตรงกับ Table
      const columns = [
        'Upload Ref.',
        'Transaction Ref.',
        'Invoice No',
        'Customer Name',
        'Financer',
        'Grand Total',
        'Payment Date',
        'Import Date',
        'Upload By',
        'Status',
        'Status Date',
        'Owner By',
        'Notes',
        'Incoming Document',
      ];

      // แปลงข้อมูลทั้งหมดที่ดึงมาสำหรับส่งออก
      const data = exportRows.map((row) => [
        row.upload_ref,
        row.transaction_ref,
        row.invoice_no,
        row.customer_name,
        row.financer,
        addCommas(row.grand_total),
        dayjs(row.payment_date).format('YYYY-MM-DD HH:mm:ss'),
        dayjs(row.import_date).format('YYYY-MM-DD HH:mm:ss'),
        row.upload_by,
        row.status === 'Upload' ? 'Pending' : row.status || '-',
        dayjs(row.status_date).format('YYYY-MM-DD HH:mm:ss'),
        row.owner_by,
        row.notes,
        row.incoming_document,
      ]);

      // สร้าง Worksheet และ Workbook
      const ws = XLSX.utils.aoa_to_sheet([columns, ...data]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report');

      // ดาวน์โหลดไฟล์
      XLSX.writeFile(wb, 'Report.xlsx');
    } catch (error) {
      alert('เกิดข้อผิดพลาดในการดาวน์โหลดข้อมูล กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div className="font-bold">{t('app.activityHistory')}</div>
      <div className="flex mt-5 ">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={t('app.startDate')}
            format="DD/MM/YYYY"
            value={startDate ? dayjs(startDate) : null} // ✅ แปลงค่าเป็น dayjs
            onChange={handleStartDateChange}
            views={['year', 'month', 'day']}
            renderInput={(params) => (
              <TextField {...params} size="small" sx={{ width: '250px' }} />
            )}
          />
        </LocalizationProvider>

        <div className="ml-5">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
               label={t('app.endDate')}
              format="DD/MM/YYYY"
              value={endDate ? dayjs(endDate) : null} // ✅ แปลงค่าเป็น dayjs
              onChange={handleEndDateChange}
              views={['year', 'month', 'day']}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ width: '250px' }} />
              )}
            />
          </LocalizationProvider>
        </div>
      </div>
      <div className="flex mt-5">
        {/* <div className="">
            <Button
              variant="contained"
              sx={{ color: 'white' }}
              onClick={() => generatePDF()}
            >
              Dowload PDF
            </Button>{' '}
          </div>{' '} */}
        <div className="">
          <Button
            variant="contained"
            sx={{ color: 'white' }}
            onClick={generateExcel}
            disabled={isExporting || isFetching || shouldSkip}
            startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('app.downloadFile')}
          </Button>
        </div>
      </div>

      <div className="px-2 mt-6">
        <TableContainer
          component={Paper}
          sx={{ maxHeight: 550, overflow: 'auto' }}
        >
          <Table stickyHeader sx={{ minWidth: 1200 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      ...customCellHeader,
                      textAlign: 'center',
                      ...(index === 0
                        ? { position: 'sticky', left: 0, zIndex: 3 }
                        : {}),
                    }}
                  >
                    <SortableTableLabel
                      column={column}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleRequestSort}
                      color="white"
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isFetching ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={15} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : _.size(reportData) ? (
              <TableBody>
                {reportData?.map((row, index) => (
                  <StyledTableRow
                    key={index}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      height: 40,
                    }}
                  >
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        backgroundColor: '#fff', // ควรใส่สีพื้นหลังด้วยเพื่อไม่ให้โปร่งใส
                        zIndex: 1, // น้อยกว่า header
                      }}
                    >
                      {row.upload_ref || '-'}
                    </TableCell>
                    <TableCell align="center">
                      {row.transaction_ref || '-'}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ ...customCell, whiteSpace: 'normal' }}
                    >
                      {Array.isArray(row.invoice_no) ? (
                        <ul
                          style={{
                            listStyleType: 'disc',
                            paddingLeft: '1.2em',
                            margin: 0,
                            textAlign: 'left',
                          }}
                        >
                          {row.invoice_no.map((inv, i) => (
                            <li key={i}>{inv}</li>
                          ))}
                        </ul>
                      ) : typeof row.invoice_no === 'string' &&
                        row.invoice_no.includes(',') ? (
                        <ul
                          style={{
                            listStyleType: 'disc',
                            paddingLeft: '1.2em',
                            margin: 0,
                            textAlign: 'left',
                          }}
                        >
                          {row.invoice_no.split(',').map((inv, i) => (
                            <li key={i}>{inv.trim()}</li>
                          ))}
                        </ul>
                      ) : (
                        row.invoice_no || '-'
                      )}
                    </TableCell>

                    <TableCell align="center">
                      {row.customer_name || '-'}
                    </TableCell>
                    <TableCell align="center" sx={customCell}>
                      {row.financer || '-'}
                    </TableCell>
                    <TableCell align="center" sx={customCell}>
                      {addCommas(row.grand_total) || '-'}
                    </TableCell>
                    <TableCell align="center">
                      {row.payment_date
                        ? dayjs(row.payment_date).format('YYYY-MM-DD HH:mm:ss')
                        : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {row.import_date
                        ? dayjs(row.import_date).format('YYYY-MM-DD HH:mm:ss')
                        : '-'}
                    </TableCell>
                    <TableCell align="center">{row.upload_by || '-'}</TableCell>
                    <TableCell align="center" sx={customCell}>
                      {row.status ? (
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '20px',
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                            color:
                              row.status === 'Upload'
                                ? '#f57c00'
                                : row.status === 'Approved'
                                ? '#2e7d32'
                                : row.status === 'Reject'
                                ? '#c62828'
                                : '#333',
                            backgroundColor:
                              row.status === 'Upload'
                                ? '#fff3e0'
                                : row.status === 'Approved'
                                ? '#e8f5e9'
                                : row.status === 'Reject'
                                ? '#ffebee'
                                : '#f0f0f0',
                            textTransform: 'capitalize',
                            width: 'fit-content',
                            margin: '0 auto',
                          }}
                        >
                          {row.status === 'Upload' ? 'Pending' : row.status}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>

                    <TableCell align="center">
                      {row.edit_at
                        ? dayjs(row.edit_at).format('YYYY-MM-DD HH:mm:ss')
                        : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {row.status_date
                        ? dayjs(row.status_date).format('YYYY-MM-DD HH:mm:ss')
                        : '-'}
                    </TableCell>
                    <TableCell align="center">{row.owner_by || '-'}</TableCell>
                    <TableCell align="center" sx={customCell}>
                      {row.notes || '-'}
                    </TableCell>
              
                  </StyledTableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={15} align="center">
                    <div>ไม่มีข้อมูล</div>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </div>
  );
}

export default Report;
